import type { Product } from '@/types';
import cn from 'classnames';
import Image from '@/components/ui/image';
import AddToCart from '@/components/cart/add-to-cart';
import placeholder from '@/assets/images/placeholders/product.svg';
import { isFree } from '@/lib/is-free';
import FreeDownloadButton from './free-download-button';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useInSuccessfulOrders } from '@/lib/hooks/use-in-orders';
import FollowButton from '../follow/follow-button';
import { useEffect, useState } from 'react';
import FavoriteButton from '../favorite/favorite-button';
import { useAuth } from '@/lib/auth';
import { useMe } from '@/data/user';
interface Props {
  product: Product;
  className?: string;
}

export default function ProductDetailsPaper({ product, className }: Props) {
  const [isSticky, setIsSticky] = useState(false);

  const { isAuthorized } = useMe();

  useEffect(() => {
    const handleScroll = () => {
      const mobileThreshold = 800;
      const desktopThreshold = 300;
      const isMobile = window.innerWidth < 768;
      const scrollThreshold = isMobile ? mobileThreshold : desktopThreshold;
      
      const wrapper = document.querySelector('.product-details-wrapper');
      if (!wrapper) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Only show sticky when scrolled past threshold AND wrapper is about to leave viewport
      setIsSticky(
        window.scrollY > scrollThreshold && 
        wrapperRect.top > windowHeight - 100
      );
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const {
    id,
    name,
    slug,
    shop,
    orders_count,
    total_downloads,
    preview_url,
    price,
    sale_price,
    is_external,
    external_product_button_text,
    external_product_url,
  } = product;

  const isFreeItem = isFree(sale_price ?? price);
  const { t } = useTranslation('common');
  // const { in_orders } = useInSuccessfulOrders(parseInt(product.id, 10));
  // const in_orders = product?.has_purchased;
  // console.log(product?.has_purchased);

  if (isAuthorized) return null; // TODO: improve this
  return (
    <div
      className={cn(
        'transition-all duration-300',
        isSticky ? [
          'fixed mb-14 md:mb-0 bottom-0 left-0 right-0 z-50',
          'bg-light-100 dark:bg-dark-250',
          'px-3 md:px-6',
          'border-t border-light-400 dark:border-dark-400',
          'py-2 md:py-3',
          'shadow-lg'
        ] : [
          'relative',
          'bg-light-100 dark:bg-dark-250',
          'px-3 md:px-6',
          'py-2 md:py-3'
        ],
        className
      )}
    >
      <div className={cn(
        'mx-auto w-full',
        isSticky ? 'max-w-screen-2xl' : ''
      )}>
        <div className={cn(
          'items-center justify-between md:flex md:w-full',
          'gap-3'
        )}>
          <div className="hidden md:block ltr:md:pr-5 rtl:md:pl-5">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-medium text-dark dark:text-light line-clamp-1">
                {name}
              </h2>
              <div className="flex items-center">
                <div className="relative flex h-6 w-6 flex-shrink-0">
                  <Image
                    alt={shop?.name}
                    quality={100}
                    src={shop?.logo?.thumbnail ?? placeholder}
                    className="rounded-full object-cover"
                    width={24}
                    height={24}
                  />
                </div>
                <h3 className="text-sm font-medium ltr:pl-2 rtl:pr-2 dark:text-dark-base md:text-dark md:dark:text-light-400">
                  {shop?.name}
                </h3>
                <div className="ml-2">
                  <FollowButton size="xs" shop_id={shop.id} />
                </div>
              </div>
            </div>
          </div>
          <div className="md:hidden">
            <h2 className="text-sm font-medium text-dark dark:text-light line-clamp-1 mb-3">
              {name}
            </h2>
          </div>
          <div className="flex items-center gap-2 sm:gap-2.5">
            {is_external ? (
              <Link
                href={external_product_url}
                target="_blank"
                className="transition-fill-colors pointer-events-auto relative flex min-h-[40px] flex-1 cursor-pointer items-center justify-center gap-2 rounded bg-brand py-2 px-3 font-semibold text-white text-sm opacity-100 duration-200 hover:bg-brand-dark focus:bg-brand-dark sm:h-10 md:px-4"
              >
                {external_product_button_text}
              </Link>
            ) : !isFreeItem ? (
              <div className="flex items-center gap-2 w-full">
              <AddToCart
                item={product}
                status={product.status}
                toastClassName="-mt-10 xs:mt-0"
                className="flex-1 w-full !h-[40px] !min-h-[30px] !content-center !items-center !justify-center !py-0 !text-center !align-middle md:!block"
              />
              
                <FavoriteButton 
                  productId={product.id}
                  iconOnly
                        className="md:!block !h-[38px] !w-[38px] !min-h-[30px] border-light-500 dark:border-dark-500 text-dark-500 dark:text-light-500"
                      />
              </div>
            ) : (
              <FreeDownloadButton
                productId={id}
                productSlug={slug}
                productName={name}
                className="flex-1"
              />
            )}
            {Boolean(preview_url) && (
              <a
                href={preview_url}
                rel="noreferrer"
                target="_blank"
                className="transition-fill-colors flex min-h-[40px] items-center justify-center gap-2 rounded border border-light-600 bg-transparent py-2 px-3 font-semibold text-dark text-sm duration-200 hover:bg-light-400 focus:bg-light-500 dark:border-dark-600 dark:text-light dark:hover:bg-dark-600 dark:focus:bg-dark-600 sm:h-10 md:px-4"
              >
                {t('text-live-preview')}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
